import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const CONDITIONS = ['new','like_new','excellent','very_good','good','fair'];

function TimeAgo({ date }) {
  const d = new Date(date);
  const diff = (Date.now() - d) / 1000;
  if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

function PostCard({ post, onOpen }) {
  return (
    <div onClick={() => onOpen(post)} style={{ background:'white', border:'1px solid var(--border)', borderRadius:14, padding:20, cursor:'pointer', transition:'box-shadow .15s, transform .15s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow='var(--shadow-md)'; e.currentTarget.style.transform='translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow=''; e.currentTarget.style.transform=''; }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {post.avatar_url
            ? <img src={post.avatar_url} alt="" style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover' }} />
            : <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--cherry)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700, fontSize:14 }}>{(post.first_name||'?')[0]}</div>
          }
          <div>
            <div style={{ fontWeight:600, fontSize:13 }}>{post.first_name} {post.last_name}</div>
            <div style={{ fontSize:11, color:'var(--text-soft)' }}><TimeAgo date={post.created_at} /></div>
          </div>
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          {post.category_icon && <span style={{ fontSize:16 }}>{post.category_icon}</span>}
          {post.response_count > 0 && (
            <span style={{ background:'var(--cherry)', color:'white', borderRadius:100, fontSize:11, fontWeight:700, padding:'2px 8px' }}>
              {post.response_count} {post.response_count===1?'reply':'replies'}
            </span>
          )}
        </div>
      </div>
      <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>{post.title}</div>
      {post.description && <div style={{ fontSize:13, color:'var(--text-mid)', lineHeight:1.5, marginBottom:8, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{post.description}</div>}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
        {post.max_budget && <span style={{ background:'#E8F5E9', color:'#2E7D32', borderRadius:100, fontSize:12, fontWeight:600, padding:'2px 10px' }}>Budget: up to ${parseFloat(post.max_budget).toLocaleString()}</span>}
        {post.condition && <span style={{ background:'var(--cream)', color:'var(--text-mid)', borderRadius:100, fontSize:12, padding:'2px 10px' }}>{post.condition.replace('_',' ')}</span>}
        {post.category_label && <span style={{ background:'#E3F2FD', color:'#1565C0', borderRadius:100, fontSize:12, padding:'2px 10px' }}>{post.category_label}</span>}
      </div>
    </div>
  );
}

export default function ISOPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('browse');
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title:'', description:'', categoryId:'', maxBudget:'', condition:'' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Detail / respond
  const [openPost, setOpenPost] = useState(null);
  const [postDetail, setPostDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [respondMsg, setRespondMsg] = useState('');
  const [respondListingId, setRespondListingId] = useState('');
  const [responding, setResponding] = useState(false);
  const [respondDone, setRespondDone] = useState(false);
  const [myListings, setMyListings] = useState([]);

  useEffect(() => {
    API.get('/categories').then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== 'browse') return;
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (filterCat) params.set('categoryId', filterCat);
    API.get(`/iso?${params}`).then(r => setPosts(r.data.posts || [])).catch(() => {}).finally(() => setLoading(false));
  }, [tab, q, filterCat]);

  useEffect(() => {
    if (tab !== 'mine' || !user) return;
    setLoading(true);
    API.get('/iso/mine').then(r => setMyPosts(r.data.posts || [])).catch(() => {}).finally(() => setLoading(false));
  }, [tab, user]);

  useEffect(() => {
    if (!openPost) return;
    setDetailLoading(true);
    setRespondDone(false);
    setRespondMsg('');
    API.get(`/iso/${openPost.id}`).then(r => setPostDetail(r.data)).catch(() => {}).finally(() => setDetailLoading(false));
    if (user) API.get('/users/me/listings?status=active').then(r => setMyListings(r.data.listings || [])).catch(() => {});
  }, [openPost, user]);

  async function handleCreate() {
    if (!form.title.trim()) { setCreateError('Title is required'); return; }
    setCreating(true); setCreateError('');
    try {
      const { data } = await API.post('/iso', {
        title: form.title,
        description: form.description || null,
        categoryId: form.categoryId || null,
        maxBudget: form.maxBudget ? parseFloat(form.maxBudget) : null,
        condition: form.condition || null,
      });
      setPosts(p => [{ ...data.post, first_name: user.first_name, last_name: user.last_name, avatar_url: user.avatar_url, response_count: 0 }, ...p]);
      setMyPosts(p => [{ ...data.post, response_count: 0 }, ...p]);
      setShowCreate(false);
      setForm({ title:'', description:'', categoryId:'', maxBudget:'', condition:'' });
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to post');
    } finally { setCreating(false); }
  }

  async function handleRespond() {
    setResponding(true);
    try {
      await API.post(`/iso/${openPost.id}/respond`, {
        message: respondMsg || null,
        listingId: respondListingId || null,
      });
      setRespondDone(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to respond');
    } finally { setResponding(false); }
  }

  async function handleClosePost(postId) {
    await API.patch(`/iso/${postId}`, { status: 'closed' }).catch(() => {});
    setMyPosts(p => p.map(x => x.id === postId ? { ...x, status:'closed' } : x));
  }

  return (
    <div style={{ maxWidth:900, margin:'0 auto', padding:'40px 24px 80px' }}>
      {/* Header */}
      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, marginBottom:8 }}>🔍 Looking For Board</h1>
        <p style={{ fontSize:15, color:'var(--text-mid)' }}>Post what you're looking for and let sellers come to you.</p>
      </div>

      {/* Tabs + Create button */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div style={{ display:'flex', gap:0, background:'var(--cream)', borderRadius:10, padding:3 }}>
          {[['browse','Browse All'], ['mine','My Posts']].map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'7px 18px', borderRadius:8, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', background: tab===t ? 'white':'transparent', color: tab===t ? 'var(--cherry)':'var(--text-mid)', boxShadow: tab===t ? 'var(--shadow-sm)':'' }}>
              {label}
            </button>
          ))}
        </div>
        {user && (
          <button className="btn btn-cherry" onClick={() => setShowCreate(true)}>+ Post ISO</button>
        )}
      </div>

      {/* Browse filters */}
      {tab === 'browse' && (
        <div style={{ display:'flex', gap:10, marginBottom:24, flexWrap:'wrap' }}>
          <input className="form-input" placeholder="Search ISO posts..." value={q} onChange={e => setQ(e.target.value)} style={{ flex:1, minWidth:200 }} />
          <select className="form-select" value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ minWidth:160 }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
          </select>
        </div>
      )}

      {/* Posts grid */}
      {loading ? (
        <div style={{ textAlign:'center', padding:48, color:'var(--text-soft)' }}>Loading...</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:16 }}>
          {(tab === 'browse' ? posts : myPosts).map(post => (
            <div key={post.id} style={{ position:'relative' }}>
              <PostCard post={post} onOpen={p => setOpenPost(p)} />
              {tab === 'mine' && post.status === 'open' && (
                <button onClick={e => { e.stopPropagation(); handleClosePost(post.id); }}
                  style={{ position:'absolute', top:14, right:14, background:'none', border:'1px solid var(--border)', borderRadius:6, padding:'2px 8px', fontSize:11, cursor:'pointer', color:'var(--text-soft)' }}>
                  Mark Closed
                </button>
              )}
              {tab === 'mine' && post.status !== 'open' && (
                <div style={{ position:'absolute', top:14, right:14, background:'#888', color:'white', borderRadius:6, padding:'2px 8px', fontSize:11 }}>Closed</div>
              )}
            </div>
          ))}
          {(tab === 'browse' ? posts : myPosts).length === 0 && (
            <div style={{ gridColumn:'1/-1', textAlign:'center', padding:48, color:'var(--text-soft)' }}>
              {tab === 'browse'
                ? 'No posts found. Be the first to post what you\'re looking for!'
                : user ? 'You haven\'t posted any ISO requests yet.' : 'Sign in to post what you\'re looking for.'
              }
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowCreate(false)}>
          <div className="modal" style={{ maxWidth:520 }}>
            <button className="modal-close" onClick={() => setShowCreate(false)}>✕</button>
            <h2 className="modal-title">Post What You're Looking For</h2>
            <p className="modal-sub" style={{ marginBottom:20 }}>Sellers will respond with matches from their inventory.</p>
            {createError && <div className="alert alert-error" style={{ marginBottom:12 }}>{createError}</div>}
            <div className="form-group">
              <label className="form-label">What are you looking for? <span className="required">*</span></label>
              <input className="form-input" placeholder="e.g. Vintage Leica M6 film camera" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} maxLength={200} />
            </div>
            <div className="form-group">
              <label className="form-label">Details</label>
              <textarea className="form-textarea" placeholder="Describe what you need — specific model, features, era, any requirements..." value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} style={{ minHeight:90 }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-select" value={form.categoryId} onChange={e => setForm(p => ({...p, categoryId: e.target.value}))}>
                  <option value="">Any category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Max Budget</label>
                <div className="price-input-wrap">
                  <span className="price-prefix">$</span>
                  <input className="price-field" type="number" placeholder="Any" min="0" step="1" value={form.maxBudget} onChange={e => setForm(p => ({...p, maxBudget: e.target.value}))} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Acceptable Condition</label>
              <select className="form-select" value={form.condition} onChange={e => setForm(p => ({...p, condition: e.target.value}))}>
                <option value="">Any condition</option>
                {CONDITIONS.map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-cherry" style={{ flex:1 }} onClick={handleCreate} disabled={creating}>
                {creating ? 'Posting...' : '🔍 Post ISO Request'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Post detail + respond modal */}
      {openPost && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setOpenPost(null)}>
          <div className="modal" style={{ maxWidth:600, maxHeight:'85vh', overflowY:'auto' }}>
            <button className="modal-close" onClick={() => setOpenPost(null)}>✕</button>
            {detailLoading ? (
              <div style={{ textAlign:'center', padding:40, color:'var(--text-soft)' }}>Loading...</div>
            ) : postDetail ? (
              <>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                  {postDetail.post.avatar_url
                    ? <img src={postDetail.post.avatar_url} alt="" style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover' }} />
                    : <div style={{ width:44, height:44, borderRadius:'50%', background:'var(--cherry)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700 }}>{(postDetail.post.first_name||'?')[0]}</div>
                  }
                  <div>
                    <div style={{ fontWeight:700, fontSize:15 }}>{postDetail.post.first_name} {postDetail.post.last_name}</div>
                    <div style={{ fontSize:12, color:'var(--text-soft)' }}>Posted <TimeAgo date={postDetail.post.created_at} /> · Expires {new Date(postDetail.post.expires_at).toLocaleDateString()}</div>
                  </div>
                </div>
                <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, fontWeight:700, marginBottom:10 }}>{postDetail.post.title}</h2>
                {postDetail.post.description && <p style={{ fontSize:14, color:'var(--text-mid)', lineHeight:1.6, marginBottom:14 }}>{postDetail.post.description}</p>}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
                  {postDetail.post.max_budget && <span style={{ background:'#E8F5E9', color:'#2E7D32', borderRadius:100, fontSize:13, fontWeight:600, padding:'4px 12px' }}>Budget: up to ${parseFloat(postDetail.post.max_budget).toLocaleString()}</span>}
                  {postDetail.post.condition && <span style={{ background:'var(--cream)', borderRadius:100, fontSize:13, padding:'4px 12px' }}>{postDetail.post.condition.replace('_',' ')}</span>}
                  {postDetail.post.category_label && <span style={{ background:'#E3F2FD', color:'#1565C0', borderRadius:100, fontSize:13, padding:'4px 12px' }}>{postDetail.post.category_icon} {postDetail.post.category_label}</span>}
                </div>

                {/* Responses */}
                {postDetail.responses?.length > 0 && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>{postDetail.responses.length} Response{postDetail.responses.length!==1?'s':''}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {postDetail.responses.map(r => (
                        <div key={r.id} style={{ background:'var(--cream)', borderRadius:10, padding:14 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                            {r.avatar_url
                              ? <img src={r.avatar_url} alt="" style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} />
                              : <div style={{ width:28, height:28, borderRadius:'50%', background:'#888', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:12 }}>{(r.first_name||'?')[0]}</div>
                            }
                            <span style={{ fontWeight:600, fontSize:13 }}>{r.first_name} {r.last_name}</span>
                            <span style={{ fontSize:11, color:'var(--text-soft)' }}><TimeAgo date={r.created_at} /></span>
                          </div>
                          {r.message && <p style={{ fontSize:13, margin:'0 0 8px 0' }}>{r.message}</p>}
                          {r.listing_title && (
                            <div style={{ display:'flex', alignItems:'center', gap:10, background:'white', borderRadius:8, padding:'8px 12px', border:'1px solid var(--border)' }}>
                              {r.listing_image && <img src={`${process.env.REACT_APP_API_URL?.replace('/api','')}${r.listing_image}`} alt="" style={{ width:48, height:48, objectFit:'cover', borderRadius:6 }} />}
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:600, fontSize:13 }}>{r.listing_title}</div>
                                {r.listing_price && <div style={{ fontSize:13, color:'var(--cherry)', fontWeight:700 }}>${parseFloat(r.listing_price).toLocaleString()}</div>}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Respond section */}
                {user && user.id !== postDetail.post.user_id && postDetail.post.status === 'open' && (
                  <div style={{ borderTop:'1px solid var(--border)', paddingTop:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>Have what they're looking for?</div>
                    {respondDone ? (
                      <div className="alert alert-success">✅ Your response was sent!</div>
                    ) : (
                      <>
                        {myListings.length > 0 && (
                          <div className="form-group">
                            <label className="form-label">Link a listing (optional)</label>
                            <select className="form-select" value={respondListingId} onChange={e => setRespondListingId(e.target.value)}>
                              <option value="">No listing — just a message</option>
                              {myListings.map(l => <option key={l.id} value={l.id}>{l.title} — ${parseFloat(l.price||0).toLocaleString()}</option>)}
                            </select>
                          </div>
                        )}
                        <div className="form-group">
                          <label className="form-label">Message</label>
                          <textarea className="form-textarea" placeholder="I have exactly what you're looking for! Here's what I have..." value={respondMsg} onChange={e => setRespondMsg(e.target.value)} style={{ minHeight:80 }} />
                        </div>
                        <button className="btn btn-cherry" onClick={handleRespond} disabled={responding || (!respondMsg.trim() && !respondListingId)}>
                          {responding ? 'Sending...' : '💬 Send Response'}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
